import React from "react";

/** Warna aksen per jenis produk — tampilan tajam saat di-screenshot web & mobile */
const themes = {
  hrms: {
    bar: "from-emerald-600/90 to-teal-700/90",
    glow: "bg-emerald-500/20",
    chip: "bg-emerald-500/30 text-emerald-100",
    sidebar: "bg-slate-900/95",
    accent: "text-emerald-400"
  },
  booking: {
    bar: "from-sky-600/90 to-cyan-700/90",
    glow: "bg-sky-500/20",
    chip: "bg-sky-500/30 text-sky-100",
    sidebar: "bg-slate-900/95",
    accent: "text-sky-400"
  },
  workhub: {
    bar: "from-violet-600/90 to-purple-800/90",
    glow: "bg-violet-500/20",
    chip: "bg-violet-500/30 text-violet-100",
    sidebar: "bg-slate-900/95",
    accent: "text-violet-400"
  },
  travel: {
    bar: "from-amber-600/90 to-orange-700/90",
    glow: "bg-amber-500/25",
    chip: "bg-amber-500/35 text-amber-50",
    sidebar: "bg-slate-950/90",
    accent: "text-amber-400"
  },
  finance: {
    bar: "from-blue-600/90 to-indigo-800/90",
    glow: "bg-blue-500/20",
    chip: "bg-blue-500/30 text-blue-100",
    sidebar: "bg-slate-900/95",
    accent: "text-blue-400"
  },
  shop: {
    bar: "from-rose-500/90 to-pink-700/90",
    glow: "bg-rose-500/20",
    chip: "bg-rose-500/30 text-rose-50",
    sidebar: "bg-slate-900/95",
    accent: "text-rose-400"
  }
};

function slugPath(title) {
  const s = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return s.slice(0, 28) || "app";
}

function PreviewHrms({ t }) {
  return (
    <div className="flex h-full text-[9px] md:text-[10px] leading-tight">
      <div className={`hidden sm:flex w-[18%] md:w-[22%] flex-col gap-1.5 p-2 border-r border-white/10 ${t.sidebar}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-2 rounded ${i === 1 ? t.chip : "bg-slate-700/50"}`} />
        ))}
      </div>
      <div className="flex-1 p-2 md:p-3 flex flex-col gap-2 min-w-0">
        <div className="grid grid-cols-3 gap-1.5">
          {[65, 42, 88].map((n, i) => (
            <div key={i} className="rounded-md bg-white/5 border border-white/10 p-2">
              <div className="h-1 w-8 bg-slate-600 rounded mb-1" />
              <div className={`text-sm font-bold ${t.accent}`}>{n}%</div>
            </div>
          ))}
        </div>
        <div className="flex-1 rounded-md bg-white/[0.04] border border-white/10 p-2">
          <div className="flex gap-2 mb-2">
            <div className={`h-4 flex-1 rounded ${t.chip}`} />
            <div className="h-4 w-12 rounded bg-slate-700/60" />
          </div>
          {[1, 2, 3, 4].map((r) => (
            <div key={r} className="flex gap-2 py-1 border-b border-white/5">
              <div className="h-2 w-1/4 rounded bg-slate-600/60" />
              <div className="h-2 flex-1 rounded bg-slate-700/40" />
              <div className="h-2 w-10 rounded bg-slate-600/40" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewBooking({ t }) {
  return (
    <div className="h-full p-2 md:p-3 flex flex-col gap-2 text-[9px] md:text-[10px]">
      <div className="flex gap-1 overflow-hidden">
        {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d, i) => (
          <div
            key={d}
            className={`shrink-0 rounded-lg px-2 py-1.5 text-center ${i === 2 ? t.chip + " font-semibold" : "bg-white/5 text-slate-400"}`}
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg bg-white/[0.06] border border-white/10 p-2 flex flex-col">
            <div className="h-8 rounded-md bg-gradient-to-br from-white/10 to-transparent mb-1" />
            <div className="h-1.5 w-2/3 rounded bg-slate-600/70 mb-1" />
            <div className={`mt-auto h-2 w-1/2 rounded ${t.glow}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewWorkhub({ t }) {
  return (
    <div className="h-full p-2 md:p-3 flex gap-1.5 md:gap-2 text-[8px] md:text-[9px]">
      {["To do", "Doing", "Done"].map((col, ci) => (
        <div key={col} className="flex-1 min-w-0 rounded-lg bg-white/[0.04] border border-white/10 p-1.5 flex flex-col gap-1.5">
          <div className={`font-semibold ${t.accent} mb-0.5`}>{col}</div>
          {(ci === 0 ? [1, 2] : ci === 1 ? [1, 2, 3] : [1]).map((i) => (
            <div key={i} className="rounded-md bg-slate-800/80 border border-white/10 p-1.5">
              <div className="h-1.5 w-full rounded bg-slate-600/60 mb-1" />
              <div className="h-1 w-3/4 rounded bg-slate-700/50" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PreviewTravel({ t }) {
  return (
    <div className="h-full flex flex-col text-[9px] md:text-[10px]">
      <div className={`px-3 py-2.5 bg-gradient-to-r ${t.bar}`}>
        <div className="h-6 rounded-lg bg-black/20 border border-white/20 flex items-center px-2 gap-2">
          <div className="h-2 w-2 rounded-full bg-white/40" />
          <div className="h-2 flex-1 rounded bg-white/15" />
        </div>
      </div>
      <div className="flex-1 p-2 md:p-3 grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg overflow-hidden border border-white/10 bg-white/[0.05]">
            <div className="h-10 bg-gradient-to-br from-amber-200/20 to-orange-500/10" />
            <div className="p-1.5">
              <div className="h-1.5 w-3/4 rounded bg-slate-500/60 mb-1" />
              <div className="h-1 w-1/2 rounded bg-slate-600/40" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewFinance({ t }) {
  return (
    <div className="h-full p-2 md:p-3 flex flex-col gap-2 text-[9px]">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white/[0.06] border border-white/10 p-2">
          <div className="h-1 w-12 bg-slate-600 rounded mb-1" />
          <div className={`text-lg font-bold ${t.accent}`}>Rp 12.4jt</div>
        </div>
        <div className="rounded-lg bg-white/[0.06] border border-white/10 p-2 flex items-end gap-0.5 h-14">
          {[40, 65, 35, 80, 55, 90, 45].map((h, i) => (
            <div key={i} className="flex-1 rounded-t bg-blue-500/50" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="flex-1 rounded-lg bg-white/[0.04] border border-white/10 p-2 flex flex-col gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex justify-between items-center py-0.5 border-b border-white/5">
            <div className="h-1.5 w-1/3 rounded bg-slate-600/50" />
            <div className="h-1.5 w-14 rounded bg-slate-700/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewShop({ t }) {
  return (
    <div className="h-full p-2 md:p-3 grid grid-cols-2 gap-2 text-[8px] md:text-[9px]">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-lg bg-white/[0.06] border border-white/10 overflow-hidden flex flex-col">
          <div className="aspect-[4/3] bg-gradient-to-br from-rose-200/15 to-pink-600/10 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/20" />
          </div>
          <div className="p-1.5 flex-1 flex flex-col">
            <div className="h-1.5 w-full rounded bg-slate-600/60 mb-1" />
            <div className={`mt-auto text-[10px] font-bold ${t.accent}`}>Rp 189k</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const bodies = {
  hrms: PreviewHrms,
  booking: PreviewBooking,
  workhub: PreviewWorkhub,
  travel: PreviewTravel,
  finance: PreviewFinance,
  shop: PreviewShop
};

/**
 * Mockup UI “tampilan web” untuk kartu proyek — vektor/CSS, tajam untuk screenshot & medsos.
 */
export default function ProjectWebPreview({ title, previewLayout = "hrms", image }) {
  const t = themes[previewLayout] || themes.hrms;
  const Body = bodies[previewLayout] || PreviewHrms;
  const path = slugPath(title);
  const hasPhoto = Boolean(image && String(image).trim());

  return (
    <div className="relative w-full overflow-hidden rounded-t-lg border-b border-slate-800/80 bg-slate-950 shadow-inner">
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] to-transparent pointer-events-none" />
      {/* Browser chrome */}
      <div className="relative flex items-center gap-2 px-2.5 py-2 bg-slate-900/95 border-b border-slate-800/90">
        <div className="flex gap-1.5 shrink-0" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/90" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/90" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/90" />
        </div>
        <div className="flex-1 min-w-0 rounded-md bg-slate-950/90 border border-slate-700/80 px-2.5 py-1 text-[10px] sm:text-[11px] text-slate-400 font-mono truncate tracking-tight">
          <span className="text-slate-500">https://</span>
          <span className="text-slate-300">app.{path}.io</span>
          <span className="text-slate-500">/dashboard</span>
        </div>
      </div>
      {/* Foto ilustrasi (jika ada) di area viewport; jika tidak, mock UI CSS */}
      <div className="relative h-[168px] sm:h-[192px] md:h-[208px]">
        {hasPhoto ? (
          <>
            <img
              src={image}
              alt={`Ilustrasi proyek: ${title}`}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-slate-950/20 pointer-events-none" />
          </>
        ) : (
          <>
            <div className={`absolute inset-0 bg-gradient-to-br ${t.bar} opacity-25`} />
            <div className="relative h-full bg-slate-950/82 backdrop-blur-[1px]">
              <Body t={t} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
